import React, { useEffect, useRef, useState } from 'react';
import type { CaptainMessage, OngoingOrderSnapshot } from '../types/events';
import type { AiChatMessage } from '../../hooks/useAiCaptainSession';
import { extractMenuIntroText } from '../../utils/aiCaptainMenuItems';
import { sanitizeCaptainDisplayText } from '../../utils/formatCaptainErrorMessage';
import { useVirtualCaptain } from '../context/VirtualCaptainContext';
import {
  CaptainAvatar,
  CaptainChatIcon,
  CaptainCloseIcon,
  CaptainMessageCard,
  CaptainUpdatesSwitch,
  CaptainVoiceButton,
} from './CaptainParts';
import CaptainAssistance from './CaptainAssistance';
import CaptainChatInput, { type CaptainChatInputHandle } from './CaptainChatInput';
import CaptainChoiceButtons from './CaptainChoiceButtons';
import CaptainDealCards from './CaptainDealCards';
import CaptainMenuItemCards from './CaptainMenuItemCards';
import CaptainOrderPlacedCard from './CaptainOrderPlacedCard';
import CaptainTableCards from './CaptainTableCards';

interface CaptainPanelProps {
  messages: CaptainMessage[];
  restaurantName: string;
  order: OngoingOrderSnapshot | null;
  voiceOn: boolean;
  onToggleVoice: () => void;
  autoOpenUpdates: boolean;
  onToggleAutoOpenUpdates: () => void;
  onClose: () => void;
  assistanceAvailable: boolean;
  chatEnabled?: boolean;
  chatMessages?: AiChatMessage[];
  streamingText?: string;
  chatConnected?: boolean;
  chatConnectionFailed?: boolean;
  onSendChat?: (text: string) => void;
  onChoiceSelect?: (messageId: string, values: string[]) => void;
  focusChat?: boolean;
  focusAssistance?: boolean;
}

function formatCaptainChatText(text: string): string {
  return sanitizeCaptainDisplayText(text.replace(/\*\*([^*]+)\*\*/g, '$1').trim());
}

/** Parse inline markdown: **bold** → <strong>. Safe — no HTML in source. */
function parseInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </>
  );
}

/**
 * Renders a completed assistant message as formatted React nodes.
 * Handles ### headings, numbered/bullet lists, **bold**, and plain paragraphs.
 * Only used for final messages — streaming text stays as plain text.
 */
function renderMarkdown(raw: string): React.ReactNode {
  const lines = raw.split('\n');
  const nodes: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ol' | 'ul' = 'ol';
  let keyIdx = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    const Tag = listType;
    nodes.push(
      <Tag key={keyIdx++} className="vc-chat-md__list">
        {listItems.map((item, i) => (
          <li key={i}>{parseInline(item)}</li>
        ))}
      </Tag>
    );
    listItems = [];
  };

  for (const line of lines) {
    const t = line.trim();

    if (!t) { flushList(); continue; }

    // ### or ## heading
    const headingMatch = t.match(/^#{1,3}\s+(.+)/);
    if (headingMatch) {
      flushList();
      nodes.push(
        <p key={keyIdx++} className="vc-chat-md__heading">{parseInline(headingMatch[1])}</p>
      );
      continue;
    }

    // Numbered list: "1. item"
    const olMatch = t.match(/^\d+\.\s+(.+)/);
    if (olMatch) {
      if (listItems.length === 0) listType = 'ol';
      listItems.push(olMatch[1]);
      continue;
    }

    // Bullet list: "- item" or "* item"
    const ulMatch = t.match(/^[-*]\s+(.+)/);
    if (ulMatch) {
      if (listItems.length === 0) listType = 'ul';
      listItems.push(ulMatch[1]);
      continue;
    }

    flushList();
    nodes.push(
      <p key={keyIdx++} className="vc-chat-md__para">{parseInline(t)}</p>
    );
  }

  flushList();

  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0];
  return <>{nodes}</>;
}

function renderChatBubbleContent(msg: AiChatMessage): React.ReactNode {
  const itemCount = msg.menuItems?.length ?? 0;
  const hasItems = itemCount > 0;

  if (hasItems) {
    const introText = extractMenuIntroText(msg.text, itemCount);
    if (!introText) return null;
    return <span className="vc-panel__chat-text">{introText}</span>;
  }

  const sanitized = sanitizeCaptainDisplayText(msg.text.trim());
  if (!sanitized) return null;

  const rendered = renderMarkdown(sanitized);
  if (!rendered) return null;

  // Wrap in a div so block elements (p, ol, ul) render correctly
  return <div className="vc-panel__chat-text vc-panel__chat-md">{rendered}</div>;
}

function renderAssistantTurn(
  msg: AiChatMessage,
  appName: string | null | undefined,
  restaurantId: number | undefined,
  onChoiceSelect?: (messageId: string, values: string[]) => void
) {
  const bubbleContent = renderChatBubbleContent(msg);
  const hasMenuItems = Boolean(msg.menuItems && msg.menuItems.length > 0);
  const hasTableItems = Boolean(msg.tableItems && msg.tableItems.length > 0);
  const hasDealItems = Boolean(msg.dealItems && msg.dealItems.length > 0);
  const hasPlacedOrder = Boolean(msg.placedOrder);
  const hasChoicePrompt = Boolean(msg.choicePrompt);
  const canShowCards = Boolean(appName && restaurantId);

  if (
    !bubbleContent &&
    !hasMenuItems &&
    !hasTableItems &&
    !hasDealItems &&
    !hasPlacedOrder &&
    !hasChoicePrompt
  ) {
    return null;
  }

  return (
    <div key={msg.id} className="vc-panel__chat-turn vc-panel__chat-turn--assistant">
      {bubbleContent && (
        <div className={`vc-panel__chat-bubble vc-panel__chat-bubble--${msg.role}`}>
          {bubbleContent}
        </div>
      )}
      {hasChoicePrompt && msg.choicePrompt && onChoiceSelect && (
        <CaptainChoiceButtons
          choice={msg.choicePrompt}
          disabled={msg.choiceAnswered}
          onSelect={(values) => onChoiceSelect(msg.id, values)}
        />
      )}
      {hasTableItems && appName && (
        <CaptainTableCards tables={msg.tableItems!} appName={appName} />
      )}
      {hasDealItems && (
        <CaptainDealCards deals={msg.dealItems!} />
      )}
      {hasMenuItems && canShowCards && (
        <CaptainMenuItemCards
          items={msg.menuItems!}
          appName={appName!}
          restaurantId={restaurantId!}
          hasMore={msg.hasMore}
        />
      )}
      {hasPlacedOrder && msg.placedOrder && appName && (
        <CaptainOrderPlacedCard order={msg.placedOrder} appName={appName} />
      )}
    </div>
  );
}

const CaptainPanel: React.FC<CaptainPanelProps> = ({
  messages,
  restaurantName,
  order,
  voiceOn,
  onToggleVoice,
  autoOpenUpdates,
  onToggleAutoOpenUpdates,
  onClose,
  assistanceAvailable,
  chatEnabled,
  chatMessages = [],
  streamingText,
  chatConnected,
  chatConnectionFailed,
  onSendChat,
  onChoiceSelect,
  focusChat = false,
  focusAssistance = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<CaptainChatInputHandle>(null);
  const [panelView, setPanelView] = useState<'chat' | 'assist'>('chat');
  const [assistSeen, setAssistSeen] = useState(false);
  const {
    streamingMenuItems = [],
    streamingTableItems = [],
    streamingDealItems = [],
    appName,
    restaurantId,
  } = useVirtualCaptain();

  const canShowMenuCards = Boolean(appName && restaurantId);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [
    messages.length,
    panelView,
    chatMessages.length,
    streamingText,
    streamingMenuItems.length,
    streamingTableItems.length,
    streamingDealItems.length,
  ]);

  useEffect(() => {
    if (focusAssistance) {
      setPanelView('assist');
      setAssistSeen(true);
    }
  }, [focusAssistance]);

  useEffect(() => {
    if (focusChat) {
      setPanelView('chat');
      chatInputRef.current?.focus();
    }
  }, [focusChat]);

  const showChat = chatEnabled && onSendChat;
  // canAssist is true when assistanceAvailable — even before an order exists (pre-order dine-in)
  const canAssist = assistanceAvailable;

  const orderLabel = order
    ? ` · Order ${order.orderCode || `#${order.orderId}`}`
    : '';

  return (
    <div className="vc-panel" role="dialog" aria-label="Peppy AI">
      <div className="vc-panel__handle" />

      <div className="vc-panel__header">
        <CaptainAvatar size={38} ring />
        <div className="vc-panel__header-text">
          <div className="vc-panel__title">Peppy AI</div>
          <div className="vc-panel__subtitle">
            {restaurantName}
            {orderLabel}
          </div>
        </div>
        <CaptainUpdatesSwitch on={autoOpenUpdates} onToggle={onToggleAutoOpenUpdates} compact />
        <CaptainVoiceButton on={voiceOn} onToggle={onToggleVoice} />
        {canAssist && (
          <div className="vc-mode-switch vc-mode-switch--panel" role="tablist" aria-label="Captain views">
            <button
              type="button"
              role="tab"
              aria-selected={panelView === 'chat'}
              className={`vc-mode-switch__btn${panelView === 'chat' ? ' vc-mode-switch__btn--active' : ''}`}
              onClick={() => setPanelView('chat')}
              title={showChat ? 'Chat' : 'Order updates'}
              aria-label={showChat ? 'Chat' : 'Order updates'}
            >
              {showChat ? <CaptainChatIcon size={15} /> : <i className="fa fa-info-circle" aria-hidden />}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={panelView === 'assist'}
              className={`vc-mode-switch__btn vc-mode-switch__btn--assist${panelView === 'assist' ? ' vc-mode-switch__btn--active' : ''}`}
              onClick={() => { setPanelView('assist'); setAssistSeen(true); }}
              title="Need assistance"
              aria-label="Need assistance"
            >
              <i className="fa fa-bell" aria-hidden />
              {!assistSeen && panelView !== 'assist' && (
                <span className="vc-mode-switch__nudge" aria-hidden />
              )}
            </button>
          </div>
        )}
        <button
          type="button"
          className="vc-panel__icon-btn vc-panel__icon-btn--close"
          onClick={onClose}
          title="Close"
          aria-label="Close"
        >
          <CaptainCloseIcon />
        </button>
      </div>

      <div className="vc-panel__messages" ref={scrollRef}>
        <div
          className={`vc-panel__messages-inner${
            panelView === 'chat' ? ' vc-panel__messages-inner--chat' : ''
          }`}
        >
          {panelView === 'assist' && canAssist ? (
            <div className="vc-panel__assistance-view">
              <CaptainAssistance order={order ?? undefined} />
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <CaptainMessageCard
                  key={msg.id}
                  type={msg.type}
                  title={msg.title}
                  text={msg.text}
                  time={msg.time}
                  eta={msg.eta}
                  animate={msg.animate}
                />
              ))}
              {showChat &&
                chatMessages.map((msg) => {
                  if (msg.role === 'user') {
                    return (
                      <div key={msg.id} className="vc-panel__chat-turn vc-panel__chat-turn--user">
                        <div className="vc-panel__chat-bubble vc-panel__chat-bubble--user">
                          {msg.text}
                        </div>
                      </div>
                    );
                  }
                  return renderAssistantTurn(msg, appName, restaurantId, onChoiceSelect);
                })}
              {showChat &&
                (streamingText ||
                  streamingMenuItems.length > 0 ||
                  streamingTableItems.length > 0 ||
                  streamingDealItems.length > 0) && (
                <div className="vc-panel__chat-turn vc-panel__chat-turn--assistant">
                  {streamingText && (
                    <div className="vc-panel__chat-bubble vc-panel__chat-bubble--assistant vc-panel__chat-bubble--streaming">
                      {formatCaptainChatText(
                        extractMenuIntroText(streamingText, streamingMenuItems.length) || streamingText
                      )}
                    </div>
                  )}
                  {streamingTableItems.length > 0 && appName && (
                    <CaptainTableCards tables={streamingTableItems} appName={appName} />
                  )}
                  {streamingDealItems.length > 0 && (
                    <CaptainDealCards deals={streamingDealItems} />
                  )}
                  {streamingMenuItems.length > 0 && canShowMenuCards && (
                    <CaptainMenuItemCards
                      items={streamingMenuItems}
                      appName={appName!}
                      restaurantId={restaurantId!}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showChat && panelView === 'chat' ? (
        <div className="vc-panel__footer">
          {chatConnectionFailed && !chatConnected && (
            <div className="vc-panel__chat-status vc-panel__chat-status--error">
              Peppy AI chat is unavailable right now. Order updates still work — try again in a moment.
            </div>
          )}
          {!chatConnectionFailed && !chatConnected && (
            <div className="vc-panel__chat-status">Connecting to Peppy…</div>
          )}
          {chatConnected && onSendChat && (
            <div className="vc-chat__quick-actions" aria-label="Quick questions">
              {([
                { label: 'Deals & Offers', message: 'Show me available deals and offers' },
                { label: 'Coupons', message: 'What coupons can I use?' },
                { label: "Today's Specials", message: "What are today's specials?" },
                { label: 'My Order', message: "What's the status of my order?" },
              ] as const).map(({ label, message }) => (
                <button
                  key={label}
                  type="button"
                  className="vc-chat__quick-action"
                  onClick={() => onSendChat(message)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <CaptainChatInput
            ref={chatInputRef}
            autoFocus={focusChat}
            disabled={!chatConnected}
            onSend={onSendChat}
          />
        </div>
      ) : (
        !showChat && !canAssist && (
          <div className="vc-panel__footer">
            <div className="vc-panel__coming-soon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
              <span>Chat coming soon…</span>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default CaptainPanel;
